import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Badge, Button, Card, CardBody, DataTable, Icon, Input, ActionsDropdown, Tooltip } from '@aidkitorg/component-library';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { useTransactionsContext, Transaction } from '../../Contexts/TransactionsContext';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { TransactionsFilter, Filters } from './components/TransactionsFilter';
import { paymentTypeToString } from '../../utils/payment_utils';
import { useHistory, useLocation } from 'react-router-dom';
import { useAccounts } from '../../Contexts/AccountsContext';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { AccountDetailsCard } from '../Accounts/components/AccountDetailsCard';
import { accountKindToName } from '../../AdminPanel/Accounts/AccountsPage';
import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { ManageBalanceRemindersModal } from '../Accounts/components/ManageBalanceRemindersModal';
import TransactionsExportModal from './components/TransactionsExportModal';

// Component for displaying truncated UUIDs with copy button
const TruncatedUuidWithCopy: React.FC<{ value: string | undefined | null }> = ({ value }) => {
  if (!value) return <span>-</span>;
  
  const truncated = value.slice(0, 8);
  
  return (
    <div className="flex items-center gap-1">
      <span className="font-mono">{truncated}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
        }}
        className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
        title="Copy full UUID"
      >
        <Icon name="ClipboardDocumentIcon" />
      </button>
    </div>
  );
};

export const TransactionsPage: React.FC = () => {
  const L = useLocalizedStrings();
  const { transactions, isLoading, fetchTransactions } = useTransactionsContext();
  const history = useHistory();
  const location = useLocation();

  // Accounts data
  const { accounts, fetchAccountsOverview, isAccountsLoading } = useAccounts();
  const [processedAccounts, setProcessedAccounts] = useState<Array<Account>>([]);
  const [manageBalanceRemindersModalOpen, setManageBalanceRemindersModalOpen] = useState<boolean>(false);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Default date range to last 15 days (computed once)
  const defaultEndDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const defaultStartDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date.toISOString().split('T')[0];
  }, []);

  // Filter state with default dates
  const [activeFilters, setActiveFilters] = useState<number>(0)  
  const [filters, setFilters] = useState<Filters>({
    transactionStart: defaultStartDate,
    transactionEnd: defaultEndDate,
    rcStatus: "",
    transactionState: "",
    entryType: "",
    paymentProvider: "",
  })

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})

  // URL parameter helpers for column visibility
  const getColumnsFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const hiddenParam = params.get('hidden');

    return {
      hidden: hiddenParam ? hiddenParam.split(',').filter(Boolean) : []
    };
  }, [location.search]);

  const updateUrlWithColumns = useCallback((visibility: Record<string, boolean>) => {
    const params = new URLSearchParams(location.search);
    const hiddenColumns = Object.entries(visibility)
      .filter(([_, visible]) => !visible)
      .map(([columnId]) => columnId);

    // Only set hidden param if there are hidden columns
    if (hiddenColumns.length > 0) {
      params.set('hidden', hiddenColumns.join(','));
    } else {
      params.delete('hidden');
    }

    const newUrl = `${location.pathname}?${params.toString()}`;
    history.replace(newUrl);
  }, [location.search, location.pathname, history]);

  // Initial fetch on mount with default date range
  useEffect(() => {
    fetchTransactions(defaultStartDate, defaultEndDate);
  }, [defaultStartDate, defaultEndDate, fetchTransactions]);

  // Fetch accounts data on mount
  useAsyncEffect(async () => {
    await fetchAccountsOverview();
  }, [fetchAccountsOverview]);

  // Check for URL parameters to auto-open modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'manage-reminders') {
      setManageBalanceRemindersModalOpen(true);
      
      // Clean up the URL parameter after opening the modal
      searchParams.delete('action');
      const newSearch = searchParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      history.replace(newUrl);
    }
  }, [location.search, location.pathname, history]);

  // Process accounts for display (similar to AccountsPage)
  useEffect(() => {
    const displayAccounts = accounts.map((account) => ({
      ...account,
      name: accountKindToName(account.kind),
      totalBalance: account.totalBalance / 100
    }));
    setProcessedAccounts(displayAccounts);
  }, [accounts]);

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.accounting,
    },
    {
      label: L.admin_panel.nav.transactions,
    },
  ];

  const columns: ColumnDef<Transaction>[] = useMemo(() => [
    {
      id: 'target_field',
      header: L.admin_panel.transactions.target_field,
      accessorKey: 'target_field',
      cell: ({ row }) => row.original.target_field || '-',
    },
    {
      id: 'payment_provider',
      header: L.admin_panel.transactions.payment_provider,
      accessorKey: 'payment_provider',
      cell: ({ row }) => paymentTypeToString(row.original.payment_provider),
      filterFn: (row, columnId, filterValue) => {
        const beautified = paymentTypeToString(row.original.payment_provider);
        return !filterValue || beautified.toLowerCase().includes(filterValue.toLowerCase());
      }
    },
    {
      id: 'entry_type',
      header: L.admin_panel.transactions.entry_type,
      accessorKey: 'entry_type',
      cell: ({ row }) => {
        switch (row.original.entry_type) {
          case 'grant_out':
          case 'grant_source':
            return <Badge variant='success' style='dot'>Grant Out</Badge>;
          case 'return_in':
            return <Badge variant='error' style='dot'>Return In</Badge>;
          default:
            return '-';
        }
      },
      filterFn: (row, columnId, filterValue) => {
        return !filterValue || row.original.entry_type === filterValue;
      }
    },
    {
      id: 'amount',
      header: L.admin_panel.transactions.amount,
      accessorKey: 'amount',
      cell: ({ row }) => {
        const amount = row.original.amount;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount);
      },
      filterFn: (row, columnId, filterValue) => {
        const amount = row.getValue(columnId) as number
        const min = filterValue[0]
        const max = filterValue[1]
  
        return amount >= min && amount <= max
      }
    },
    {
      id: 'subledger',
      header: L.admin_panel.transactions.subledger,
      accessorKey: 'subledger',
      cell: ({ row }) => row.original.subledger || '-',
    },
    {
      id: 'action_id',
      header: L.admin_panel.transactions.action_id,
      accessorKey: 'action_id',
      cell: ({ row }) => <TruncatedUuidWithCopy value={row.original.action_id} />,
    },
    {
      id: 'attempt',
      header: L.admin_panel.transactions.attempt,
      accessorKey: 'attempt',
      cell: ({ row }) => row.original.attempt || '-',
    },
    {
      id: 'rc_status',
      header: L.admin_panel.transactions.rc_status,
      accessorKey: 'rc_status',
      cell: ({ row }) => {
        switch (row.original.rc_status) {
          case 'PROPOSED':
            return <Badge variant='warning'>Proposed</Badge>;
          case 'ANTICIPATED':
            return <Badge variant='warning'>Anticipated</Badge>;
          case 'PENDING':
            return <Badge variant='info'>Pending</Badge>;
          case 'SCHEDULED':
            return <Badge variant='warning'>Scheduled</Badge>;
          case 'FAILED':
            return <Badge variant='error'>Failed</Badge>;
          case 'CANCELLED':
            return <Badge variant='error'>Cancelled</Badge>;
          case 'COMPLETE':
            return <Badge variant='success'>Complete</Badge>;
          default:
            return '-';
        }
      },
      filterFn: (row, columnId, filterValue) => {
        return !filterValue || row.original.rc_status === filterValue;
      }
    },
    {
      id: 'transaction_id',
      header: L.admin_panel.transactions.transaction_id,
      accessorKey: 'transaction_id',
      cell: ({ row }) => <TruncatedUuidWithCopy value={row.original.transaction_id} />,
    },
    {
      id: 'transaction_state',
      header: L.admin_panel.transactions.transaction_state,
      accessorKey: 'transaction_state',
      cell: ({ row }) => row.original.transaction_state || '-',
      filterFn: (row, columnId, filterValue) => {
        return !filterValue || row.original.transaction_state === filterValue;
      }
    },
    {
      id: 'transaction_clear_date',
      accessorKey: 'transaction_clear_date',
      header: L.admin_panel.transactions.transaction_date,
      cell: ({ row }) => {
        if (!row.original.transaction_clear_date) return '-';
        const date = new Date(row.original.transaction_clear_date);
        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(date);
      },
      filterFn: (row, columnId, filterValue) => {
        return true; // Dates are handles by the backend query - we fetch new each time. 
      },
      enableSorting: true,
    },
    {
      id: 'card_id',
      header: L.admin_panel.transactions.card_id,
      accessorKey: 'card_id',
      cell: ({ row }) => row.original.card_id || '-',
    },
    {
      id: 'trace_id',
      header: L.admin_panel.transactions.trace_id,
      accessorKey: 'trace_id',
      cell: ({ row }) => row.original.trace_id || '-',
    },
    {
      id: 'failure_message',
      header: L.admin_panel.transactions.failure_message,
      accessorKey: 'failure_message',
      cell: ({ row }) => {
        if (!row.original.failure_message) return '-';
        return (
          <div className="text-sm text-red-600 truncate max-w-xs" title={row.original.failure_message}>
            {row.original.failure_message}
          </div>
        );
      },
    },
    {
      id: 'givecard_recipient_id',
      header: L.admin_panel.transactions.givecard_recipient_id,
      accessorKey: 'givecard_recipient_id',
      cell: ({ row }) => row.original.givecard_recipient_id || '-',
    },
    {
      id: "actions",
      header: L.admin_panel.payments.actions,
      size: 90,
      minSize: 90,
      maxSize: 90,
      cell: ({ row }) => {
        const uuid = row.original.uid
        const actions: TableAction<Transaction>[] = [
          {
            label: L.admin_panel.transactions.view_payments,
            onClick: () => {
              window.open('/admin-panel/payments/' + uuid, '_blank')
            },
            icon: 'ArrowTopRightOnSquareIcon'
          },
          {
            label: L.admin_panel.payments.copy_uid,
            onClick: () => {
              navigator.clipboard.writeText(uuid || '')
            },
            icon: 'ClipboardDocumentIcon'
          }
        ]
        // Link directly to new applicant-level Payments page
        return <ActionsDropdown item={row.original} actions={actions} anchor="bottom end" />
      },
      enableSorting: false,
      enablePinning: true,
    }
  ], [L]);

  // Define default hidden columns (columns NOT in the default view)
  const DEFAULT_HIDDEN_COLUMNS = ['subledger', 'attempt', 'card_id', 'trace_id', 'failure_message', 'givecard_recipient_id'];

  // Initialize column visibility from URL or set defaults
  useEffect(() => {
    const urlData = getColumnsFromUrl();
    if (urlData.hidden && urlData.hidden.length > 0) {
      const allColumnIds = columns.map(col => col.id).filter(Boolean) as string[];
      const validHiddenColumns = urlData.hidden.filter(col => allColumnIds.includes(col));
      
      const initialVisibility = Object.fromEntries(
        allColumnIds.map(colId => [colId, !validHiddenColumns.includes(colId)])
      );
      setColumnVisibility(initialVisibility);
    } else {
      // Set default visibility if no URL params
      const allColumnIds = columns.map(col => col.id).filter(Boolean) as string[];
      const initialVisibility = Object.fromEntries(
        allColumnIds.map(colId => [colId, !DEFAULT_HIDDEN_COLUMNS.includes(colId)])
      );
      setColumnVisibility(initialVisibility);
    }
  }, [getColumnsFromUrl, columns]);

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback((visibility: Record<string, boolean>) => {
    setColumnVisibility(visibility);
    updateUrlWithColumns(visibility);
  }, [updateUrlWithColumns]);

  // Convert visibility state to hiddenCols for DataTable
  const hiddenColumns = Object.entries(columnVisibility)
    .filter(([_, visible]) => !visible)
    .map(([columnId]) => columnId);

  // Compute visible fields in the order of column definitions, using accessorKey
  const visibleExportFields: string[] = useMemo(() => {
    return (columns as ColumnDef<Transaction>[]) 
      .filter(col => col.id && col.id !== 'actions')
      .filter(col => {
        const id = col.id as string;
        const isVisible = columnVisibility.hasOwnProperty(id) ? columnVisibility[id] : !DEFAULT_HIDDEN_COLUMNS.includes(id);
        return isVisible !== false;
      })
      .map(col => (col as any).accessorKey)
      .filter((k: unknown) => typeof k === 'string');
  }, [columns, columnVisibility]);

  const transactionsEmpty = !isLoading && transactions.length === 0;
  const transactionsEmptyState = (
    <div className="py-12 text-center text-gray-600">
      <div className="text-lg font-medium text-gray-700 mb-2">
        No transactions found
      </div>
      <div className="text-sm">
        No transactions found for the selected date range.
      </div>
    </div>
  );

  // Create account details cards (sorted by balance, descending)
  const accountDetailsCards = processedAccounts
    .sort((a, b) => b.totalBalance - a.totalBalance)
    .map((account, idx) => {
      return (
        <div key={idx}>
          <AccountDetailsCard index={idx} account={account} className="h-full" excludePieChart={true}/>
        </div>
      )
    })

  return (
    <AdminPanelPage
      fullWidth={isTableExpanded}
      title={L.admin_panel.nav.transactions}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
          <Button
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            variant="secondary"
            aria-label={L.admin_panel.payments.refresh_data}
            onClick={async () => {
              await fetchTransactions(filters.transactionStart, filters.transactionEnd)
              await fetchAccountsOverview(true)
            }}
          />
        </Tooltip>,
        <Button
          variant="primary"
          icon={<Icon name="CalendarIcon" type="solid" role="decorative" />}
          onClick={() => setManageBalanceRemindersModalOpen(true)}
          aria-label={L.admin_panel.accounts.manage_reminders}
        >
          {L.admin_panel.accounts.manage_reminders}
        </Button>
      ]}
    >
      <div className="w-full space-y-4">
        {/* Account balance cards */}
        {processedAccounts.length > 0 && (
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-stretch">
            {accountDetailsCards}
          </div>
        )}
        
        <Card>
          <CardBody>
            {transactionsEmpty ? transactionsEmptyState : (
              <DataTable
                enableSearch
                searchLabel={L.admin_panel.transactions.search_transactions}
                dataFilteredOutMessage={L.admin_panel.transactions.no_transactions_match_filter}
                columns={columns}
                data={transactions}
                loading={isLoading}
                filters={(table) => (
                  <TransactionsFilter
                    filters={filters}
                    setFilters={setFilters}
                    table={table}
                    setActiveFilters={setActiveFilters}
                    onApplyFilters={fetchTransactions}
                    defaultStartDate={defaultStartDate}
                    defaultEndDate={defaultEndDate}
                  />
                )}
                activeFilters={activeFilters}
                showPagination
                hiddenCols={hiddenColumns}
                enableColumnVisibility={true}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                columnPinningState={{
                  right: ['actions']
                }}
                resizableColumns
                headerActionButtons={[
                  <Tooltip text={L.admin_panel.payments.export_recipients} asChild>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setExportModalOpen(true)}
                      icon={<Icon name="ArrowDownTrayIcon" role="decorative" type="solid" />}
                      aria-label={L.admin_panel.payments.export_recipients}
                    />
                  </Tooltip>,
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setIsTableExpanded(prev => (!prev))}
                    icon={<Icon
                      role="decorative"
                      type="solid"
                      name={isTableExpanded ? "ArrowsPointingInIcon" : "ArrowsPointingOutIcon"}
                    />}
                    title={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
                    aria-label={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
                  />
                ]}
              />
            )}
          </CardBody>
        </Card>
      </div>

      <ManageBalanceRemindersModal
        remindersModalOpen={manageBalanceRemindersModalOpen}
        setRemindersModalOpen={setManageBalanceRemindersModalOpen}
      />
      <TransactionsExportModal open={exportModalOpen} setOpen={setExportModalOpen} transactions={transactions} visibleFields={visibleExportFields} />
    </AdminPanelPage>
  );
};

export default TransactionsPage;
